import Dialog from "../../components/General/ConfirmableDialog";
import { useState, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Drawer,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { DataGrid } from "@mui/x-data-grid";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import FileUploadModal from "../../components/Fleet/FileUploadModal/FileUploadModal";
import { handleDownloadFile } from "../../api/handleDownloadFile";
import { handleViewFile } from "../../api/handleViewFile";
import { EmployeePersonalFileDto } from "../../interfaces/HR/employeePersonalFile";
import { DeliveryUnit } from "../../data/HR/DeliveryUnit";

const departments = [
  "All",
  "Dispatch",
  "Brokerage",
  "Safety",
  "Accounting",
  "Data Analytics Department",
  "HR",
  "Permits",
  "IT",
  "Management",
  "Operations",
  "Fleet",
  "Truck Sales",
  "Insurance",
  "Intermodal",
];

const fetchEmployeeFolders = async (
  token: string | null | undefined,
  areActive: boolean,
) => {
  const { data } = await axios.get(
    `${apiVariable}/getEmployeeFolders/${areActive}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchEmployeePersonalFiles = async (
  token: string | null | undefined,
  folderId: string,
) => {
  const { data } = await axios.get(
    `${apiVariable}/getEmployeePersonalFiles/${folderId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const deleteEmployeePersonalFile = async (
  token: string | null | undefined,
  fileId: string,
) => {
  await axios.delete(`${apiVariable}/deleteEmployeePersonalFile/${fileId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const updateEmployeePersonalFile = async (
  token: string | null | undefined,
  file: EmployeePersonalFileDto,
) => {
  await axios.put(`${apiVariable}/updateEmployeePersonalFile`, file, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const EmployeeFoldersPage = () => {
  const { token } = useAuth();
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("");
  const [documentDate, setDocumentDate] = useState<number>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any>(null);
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [areActive, setAreActive] = useState(true);
  const [viewLoadingId, setViewLoadingId] = useState<string | number | null>(
    null,
  );
  const [downloadLoadingId, setDownloadLoadingId] = useState<
    string | number | null
  >(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [fileToEdit, setFileToEdit] = useState<EmployeePersonalFileDto | null>(
    null,
  );
  const [editCategory, setEditCategory] = useState("");
  const [editDocumentDate, setEditDocumentDate] = useState<number>();
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");
  const [selectedDeliveryUnit, setSelectedDeliveryUnit] = useState<string>("");

  const {
    data: folders,
    isLoading: isLoadingFolders,
    isError: isErrorFolders,
    error: foldersError,
  } = useQuery({
    queryKey: ["employeeFolders", areActive],
    queryFn: () => fetchEmployeeFolders(token, areActive),
    enabled: !!token,
  });

  const {
    data: personalFiles,
    isLoading: isLoadingFiles,
    isError: isErrorFiles,
    error: filesError,
    refetch: refetchFiles,
  } = useQuery({
    queryKey: ["employeePersonalFiles", selectedFolder?.id],
    queryFn: () => fetchEmployeePersonalFiles(token, selectedFolder?.id),
    enabled: !!selectedFolder?.id && !!token,
  });

  const handleFolderClick = (folder: any) => {
    setSelectedFolder(folder);
    setDrawerOpen(true);
  };

  const filesWithHandler = useMemo(() => {
    if (!personalFiles || !selectedFolder) return [];
    return personalFiles.map((f: any) => ({
      ...f,
      id: f.id,
      firstName: selectedFolder.firstName,
      lastName: selectedFolder.lastName,
      fileName: f.fileName,
      token: token,
    }));
  }, [personalFiles, selectedFolder, token]);

  const handleOpenCategoryDialog = () => {
    setUploadCategory("");
    setOpenCategoryDialog(true);
  };

  const handleCategoryDialogClose = () => {
    setOpenCategoryDialog(false);
  };

  const handleCategorySubmit = () => {
    if (uploadCategory) {
      setOpenCategoryDialog(false);
      setOpenUploadModal(true);
    }
  };

  const handleDeleteClick = (file: any) => {
    setFileToDelete(file);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (fileToDelete && fileToDelete.id) {
      try {
        await deleteEmployeePersonalFile(token, fileToDelete.id);
        await refetchFiles();
        setOpenDeleteDialog(false);
        setFileToDelete(null);
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setFileToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleEditClick = (file: EmployeePersonalFileDto) => {
    setFileToEdit(file);
    setEditCategory(file.category);
    setEditDocumentDate(file.documentDate);
    setOpenEditDialog(true);
  };

  const handleEditCancel = () => {
    setFileToEdit(null);
    setOpenEditDialog(false);
  };

  const handleEditSave = async () => {
    if (!fileToEdit || !editCategory || !editDocumentDate) return;
    try {
      await updateEmployeePersonalFile(token, {
        ...fileToEdit,
        category: editCategory,
        documentDate: editDocumentDate,
      });
      await refetchFiles();
    } catch (error) {
      console.error("Error updating file:", error);
    } finally {
      handleEditCancel();
    }
  };

  const employeeFilesColumns = [
    { field: "category", headerName: "Category", flex: 1 },
    { field: "documentDate", headerName: "Document Date", flex: 1 },
    {
      field: "viewAction",
      headerName: "View",
      renderCell: (params: any) => {
        const { row } = params;
        const isLoading = viewLoadingId === row.id;
        return (
          <Button
            variant="contained"
            onClick={async () => {
              setViewLoadingId(row.id);
              try {
                await handleViewFile(row, "personalFolder", row.token);
              } finally {
                setViewLoadingId(null);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "View"
            )}
          </Button>
        );
      },
      flex: 0.6,
    },
    {
      field: "downloadAction",
      headerName: "Download",
      renderCell: (params: any) => {
        const { row } = params;
        const isLoading = downloadLoadingId === row.id;
        return (
          <Button
            variant="contained"
            onClick={async () => {
              setDownloadLoadingId(row.id);
              try {
                await handleDownloadFile(row, "personalFolder", row.token);
              } finally {
                setDownloadLoadingId(null);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Download"
            )}
          </Button>
        );
      },
      flex: 0.6,
    },
    {
      field: "editAction",
      headerName: "Edit",
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <Button variant="contained" onClick={() => handleEditClick(row)}>
            Edit
          </Button>
        );
      },
      flex: 0.5,
    },
    {
      field: "deleteAction",
      headerName: "Delete",
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteClick(row)}
          >
            Delete
          </Button>
        );
      },
      flex: 0.5,
    },
  ];

  const filteredFolders = useMemo(() => {
    if (!folders) return [];
    return folders.filter((folder: any) => {
      const firstNameMatch = folder.firstName
        ?.toLowerCase()
        .includes(searchFirstName.toLowerCase());
      const lastNameMatch = folder.lastName
        ?.toLowerCase()
        .includes(searchLastName.toLowerCase());
      const departmentMatch =
        selectedDepartment === "All" ||
        folder.department?.toLowerCase() === selectedDepartment.toLowerCase();
      const deliveryUnitMatch =
        !selectedDeliveryUnit ||
        folder.deliveryUnit?.toLowerCase() ===
          selectedDeliveryUnit.toLowerCase();
      return (
        firstNameMatch && lastNameMatch && departmentMatch && deliveryUnitMatch
      );
    });
  }, [
    folders,
    searchFirstName,
    searchLastName,
    selectedDepartment,
    selectedDeliveryUnit,
  ]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  const categoryOptions = [
    "Cerere de angajare",
    "Ordin de angajare",
    "Ordin de concediu",
    "Contract individual de munca",
    "Contract de confidentialitate",
    "Copie dupa actul de identitate",
    "Contract de raspundere materiala",
    "Oferta de munca",
    "Preavizare IT Park",
    "Constimtamant",
    "Fisa postului",
    "Acord aditional",
    "Altele",
  ];

  return (
    <Box className="employee-folders-page" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          Employee Folders
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={areActive}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={`Showing ${areActive ? "Active" : "Inactive"} Employees`}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            display: "grid",
            width: "100%",
            maxWidth: { xs: "100%", sm: 496, lg: 928 },
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 4,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <TextField
            label="Search by First Name"
            variant="outlined"
            value={searchFirstName}
            onChange={(e) => setSearchFirstName(e.target.value)}
          />
          <TextField
            label="Search by Last Name"
            variant="outlined"
            value={searchLastName}
            onChange={(e) => setSearchLastName(e.target.value)}
          />
          <FormControl fullWidth sx={{ minWidth: 0 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDepartment}
              label="Department"
              onChange={(e) => {
                setSelectedDepartment(e.target.value);
                setSelectedDeliveryUnit("");
              }}
            >
              {departments.map((department) => (
                <MenuItem key={department} value={department}>
                  {department}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ minWidth: 0 }}>
            <InputLabel>Delivery Unit</InputLabel>
            <Select
              value={selectedDeliveryUnit}
              label="Delivery Unit"
              onChange={(e) => setSelectedDeliveryUnit(e.target.value)}
            >
              {DeliveryUnit.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {isLoadingFolders ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isErrorFolders ? (
        <Box className="table-error">
          <Typography color="error">
            Error: {(foldersError as Error).message}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {folders?.length === 0 && (
            <Grid item xs={12}>
              <Typography>No folders found.</Typography>
            </Grid>
          )}
          {filteredFolders?.map((folder: any) => (
            <Grid item xs={12} sm={4} md={3} key={folder.id}>
              <Card>
                <CardActionArea onClick={() => handleFolderClick(folder)}>
                  <CardContent>
                    <Typography variant="h6">
                      {folder.firstName} {folder.lastName}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedFolder(null);
        }}
        sx={{ "& .MuiDrawer-paper": { top: 64, height: "calc(100% - 64px)" } }}
      >
        <Box sx={{ maxWidth: "100%", width: 800, p: 2 }}>
          <Button
            onClick={() => {
              setDrawerOpen(false);
              setSelectedFolder(null);
            }}
            sx={{ display: { xs: "inline-flex", lg: "none" }, mb: 1 }}
          >
            Close employee files
          </Button>
          {selectedFolder && (
            <>
              <Typography variant="h5">
                {selectedFolder.firstName} {selectedFolder.lastName}'s Files
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2, mb: 2 }}
                onClick={handleOpenCategoryDialog}
              >
                Upload File
              </Button>

              {isLoadingFiles ? (
                <CircularProgress />
              ) : isErrorFiles ? (
                <Typography color="error">
                  Error: {(filesError as Error).message}
                </Typography>
              ) : (
                <DataGrid
                  rows={filesWithHandler}
                  columns={employeeFilesColumns}
                  autoHeight
                />
              )}
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={openCategoryDialog} onClose={handleCategoryDialogClose}>
        <DialogTitle>Select File Metadata</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              inputValue={uploadCategory}
              onInputChange={(_, value) => setUploadCategory(value)}
              renderInput={(params) => (
                <TextField {...params} label="Category" fullWidth />
              )}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="file-date-label">Document Date</InputLabel>
            <Select
              labelId="file-date-label"
              value={documentDate}
              label="Document Date"
              onChange={(e) =>
                setDocumentDate(e.target.value as unknown as number)
              }
            >
              <MenuItem value={2020}>2020</MenuItem>
              <MenuItem value={2021}>2021</MenuItem>
              <MenuItem value={2022}>2022</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2026}>2026</MenuItem>
              <MenuItem value={2027}>2027</MenuItem>
              <MenuItem value={2028}>2028</MenuItem>
              <MenuItem value={2029}>2029</MenuItem>
              <MenuItem value={2030}>2030</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryDialogClose}>Cancel</Button>
          <Button onClick={handleCategorySubmit} disabled={!uploadCategory}>
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {openUploadModal && selectedFolder && (
        <FileUploadModal
          open={openUploadModal}
          onClose={() => {
            setOpenUploadModal(false);
            refetchFiles();
          }}
          row={{
            id: selectedFolder.id,
          }}
          type="personalFolder"
          employeeName={`${selectedFolder.firstName} ${selectedFolder.lastName}`}
          employeeFileCategory={uploadCategory}
          employeeDocumentDate={documentDate}
        />
      )}

      <Dialog open={openEditDialog} onClose={handleEditCancel}>
        <DialogTitle>Edit File Metadata</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              inputValue={editCategory}
              onInputChange={(_, value) => setEditCategory(value)}
              renderInput={(params) => (
                <TextField {...params} label="Category" fullWidth />
              )}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="edit-file-date-label">Document Date</InputLabel>
            <Select
              labelId="edit-file-date-label"
              value={editDocumentDate}
              label="Document Date"
              onChange={(e) =>
                setEditDocumentDate(e.target.value as unknown as number)
              }
            >
              <MenuItem value={2020}>2020</MenuItem>
              <MenuItem value={2021}>2021</MenuItem>
              <MenuItem value={2022}>2022</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2026}>2026</MenuItem>
              <MenuItem value={2027}>2027</MenuItem>
              <MenuItem value={2028}>2028</MenuItem>
              <MenuItem value={2029}>2029</MenuItem>
              <MenuItem value={2030}>2030</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            disabled={!editCategory || !editDocumentDate}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the file ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>No</Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeFoldersPage;
